import React from 'react';
import { isAppleDevice } from '../lib/device';

/**
 * Strip unit/suite designators so OpenStreetMap's search resolves the street
 * address instead of failing on "Suite 200" / "Ste B" / "#500". Mirrors the
 * `osmQuery` Eleventy filter. Google and Apple Maps get the full address.
 */
function osmQuery(address) {
    return String(address)
        .replace(/\b(?:suite|ste|unit)\b\.?\s*#?\s*[A-Za-z0-9-]+/gi, ' ')
        .replace(/#\s*[A-Za-z0-9-]+/g, ' ')
        .replace(/\s*,\s*,/g, ',')
        .replace(/\s{2,}/g, ' ')
        .replace(/\s+,/g, ',')
        .replace(/,\s*$/, '')
        .trim();
}

/**
 * LocationLinks — privacy-friendly map buttons keyed on a free-text address.
 * Mirrors the server-rendered `partials/location-links.njk` macro: instead of
 * embedding a third-party map, it opens OpenStreetMap, Google Maps, and — on
 * Apple devices only — Apple Maps in a new tab. Renders nothing when no address
 * is available. No external request happens until a button is clicked.
 */
export default function LocationLinks({ address, name, className = '' }) {
    if (!address) return null;

    const full = encodeURIComponent(address);
    const osm = encodeURIComponent(osmQuery(address));
    const apple = isAppleDevice();

    return (
        <div className={`location-links flex flex-wrap items-center gap-2 ${className}`}>
            <a
                href={`https://www.openstreetmap.org/search?query=${osm}`}
                title={`Open ${name || address} in OpenStreetMap`}
                target="_blank"
                rel="noopener noreferrer"
                className="map-btn"
            >
                OpenStreetMap
            </a>
            <a
                href={`https://www.google.com/maps/search/?api=1&query=${full}`}
                title={`Open ${name || address} in Google Maps`}
                target="_blank"
                rel="noopener noreferrer"
                className="map-btn"
            >
                Google Maps
            </a>
            {apple && (
                <a
                    href={`https://maps.apple.com/?q=${full}`}
                    title={`Open ${name || address} in Apple Maps`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="map-btn"
                >
                    Apple Maps
                </a>
            )}
        </div>
    );
}
